import CloudUploadOutlined from '@mui/icons-material/CloudUploadOutlined';
import FileUploadOutlined from '@mui/icons-material/FileUploadOutlined';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { useState } from 'react';
import { FileUploader } from 'react-drag-drop-files';

import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { ImageUploaderDashedBox } from '~pages/Approvals/Files/components/TicketDrawer/styled';

import { ALLOWED_IMAGE_EXTENSIONS, MAX_IMAGE_SIZE_IN_MB } from './timesheetImageUpload';

export const UPLOAD_DROP_ZONE_HEIGHT = '300px';

function UploadDropZone({
  isUploadingImage,
  onAddImage,
  onTypeError,
}: {
  isUploadingImage: boolean;
  onAddImage: (file: File) => void;
  onTypeError?: () => void;
}) {
  const [isDragging, setIsDragging] = useState(false);

  return (
    <FileUploader
      disabled={isUploadingImage}
      dropMessageStyle={{ opacity: 0 }}
      handleChange={onAddImage}
      name="timesheet-image-uploader"
      onDraggingStateChange={setIsDragging}
      onTypeError={onTypeError}
      types={ALLOWED_IMAGE_EXTENSIONS}
    >
      <ImageUploaderDashedBox
        isDisabled={isUploadingImage}
        sx={(theme) => ({
          height: UPLOAD_DROP_ZONE_HEIGHT,
          '&:hover': isUploadingImage
            ? {}
            : { borderColor: theme.brandV2.colors.treadYellowDark },
          ...(isDragging && { borderColor: theme.brandV2.colors.treadOrangeDark }),
        })}
      >
        {isUploadingImage ? (
          <>
            <CircularProgress size={32} />
            <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
              {t('ticket_drawer_v2.image_uploader.uploading_image')}
            </Typography>
          </>
        ) : (
          <>
            {isDragging ? (
              <CloudUploadOutlined
                sx={(theme) => ({
                  color: theme.brandV2.colors.treadBlack,
                  fontSize: '40px',
                })}
              />
            ) : (
              <FileUploadOutlined
                sx={(theme) => ({
                  color: theme.brandV2.colors.treadGray6,
                  fontSize: '40px',
                })}
              />
            )}
            <Typography
              sx={(theme) => ({
                color: theme.brandV2.colors.treadBlack,
                fontSize: '14px',
                fontWeight: 600,
                textAlign: 'center',
              })}
            >
              {t('file_upload.click_add_or_drag')}
            </Typography>
            <Typography
              sx={(theme) => ({
                color: theme.brandV2.colors.treadGray3,
                fontSize: '12px',
                fontWeight: 600,
                textAlign: 'center',
              })}
            >
              {t('approvals.timesheets.upload_hint', {
                extensions: ALLOWED_IMAGE_EXTENSIONS.join(', ').toUpperCase(),
                maxSize: MAX_IMAGE_SIZE_IN_MB,
              })}
            </Typography>
            <SmallButton color="brandV2Yellow" component="span" sx={{ mt: 1 }}>
              {t('file_upload.upload')}
            </SmallButton>
          </>
        )}
      </ImageUploaderDashedBox>
    </FileUploader>
  );
}

interface TimesheetImageProps {
  fileUrl: string | null;
  isUploadingImage: boolean;
  mimeType: string | null;
  onAddImage?: (file: File) => void;
  onTypeError?: () => void;
  previewUrl: string | null;
}

export default function TimesheetImage({
  fileUrl,
  isUploadingImage,
  mimeType,
  onAddImage,
  onTypeError,
  previewUrl,
}: TimesheetImageProps) {
  if (!fileUrl) {
    if (onAddImage) {
      return (
        <UploadDropZone
          isUploadingImage={isUploadingImage}
          onAddImage={onAddImage}
          onTypeError={onTypeError}
        />
      );
    }

    return (
      <Box
        sx={{
          alignItems: 'center',
          bgcolor: (theme) => theme.semantic.surfaceSunken,
          display: 'flex',
          justifyContent: 'center',
          minHeight: 320,
          width: '100%',
        }}
      >
        <Typography sx={{ color: 'text.secondary' }}>
          {t('approvals.timesheets.no_image')}
        </Typography>
      </Box>
    );
  }

  const isPdf = mimeType === 'application/pdf';

  if (isPdf) {
    return (
      <Box
        component="iframe"
        src={previewUrl ?? fileUrl}
        title={t('approvals.timesheets.timesheet_image')}
        sx={{ border: 'none', height: '100%', minHeight: 480, width: '100%' }}
      />
    );
  }

  return (
    <Box
      component="img"
      alt={t('approvals.timesheets.timesheet_image')}
      src={fileUrl}
      sx={{ height: 'auto', maxWidth: '100%', objectFit: 'contain' }}
    />
  );
}
